import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";
import { api } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

const STORAGE_KEY = "stayzen_concierge_session";

const T = {
  pt: {
    trigger: "Fale connosco",
    header: "StayZen Concierge",
    subtitle: "Consultor virtual · Claude AI",
    placeholder: "Escreva a sua pergunta...",
    send: "Enviar",
    intro:
      "Olá. Sou o Concierge da StayZen. Posso responder a perguntas sobre gestão da sua casa, receitas esperadas, licença AL ou marcação de reunião. Como o posso ajudar?",
    thinking: "A pensar…",
    error: "Não consegui responder agora. Tente novamente.",
    book_meeting: "Marcar reunião",
    powered: "Respostas por Claude Sonnet 4.5",
  },
  en: {
    trigger: "Talk to us",
    header: "StayZen Concierge",
    subtitle: "Virtual consultant · Claude AI",
    placeholder: "Type your question...",
    send: "Send",
    intro:
      "Hello. I'm the StayZen Concierge. I can answer questions about managing your home, expected revenue, AL licensing, or setting up a meeting. How can I help?",
    thinking: "Thinking…",
    error: "I couldn't answer right now. Please try again.",
    book_meeting: "Book a meeting",
    powered: "Answers by Claude Sonnet 4.5",
  },
};

const ConciergeChat = () => {
  const { lang } = useLang();
  const t = T[lang] || T.pt;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: t.intro }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [suggestMeeting, setSuggestMeeting] = useState(false);
  const scrollRef = useRef(null);

  // Reset intro when language changes
  useEffect(() => {
    setMessages([{ role: "assistant", text: T[lang]?.intro || T.pt.intro }]);
    setSuggestMeeting(false);
  }, [lang]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    setSending(true);
    try {
      const sessionId = localStorage.getItem(STORAGE_KEY) || null;
      const { data } = await api.post("/concierge/chat", {
        session_id: sessionId,
        message: msg,
        lang,
      });
      if (data.session_id) localStorage.setItem(STORAGE_KEY, data.session_id);
      setMessages((prev) => [...prev, { role: "assistant", text: data.reply }]);
      if (data.suggest_meeting) setSuggestMeeting(true);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", text: t.error, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const gotoMeeting = () => {
    setOpen(false);
    // Scroll or navigate — the marketing site has anchors #contact and #meeting
    setTimeout(() => {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  return (
    <>
      {/* FLOATING TRIGGER */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-40 group flex items-center gap-3 bg-[#0A1128] text-[#F4F1EB] hover:bg-[#162243] transition-all duration-300 shadow-2xl shadow-[#0A1128]/25 pl-4 pr-5 py-3.5 rounded-full border border-[#C5A059]/40"
          data-testid="concierge-trigger"
          aria-label={t.trigger}
        >
          <span className="relative inline-flex">
            <span className="absolute inset-0 rounded-full bg-[#C5A059]/40 animate-ping" />
            <MessageCircle size={18} strokeWidth={1.6} className="relative text-[#C5A059]" />
          </span>
          <span className="text-xs tracking-[0.22em] uppercase">{t.trigger}</span>
        </button>
      )}

      {/* CHAT PANEL */}
      {open && (
        <div
          className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-50 w-[calc(100vw-3rem)] max-w-md h-[560px] max-h-[80vh] bg-[#FCFAF8] border border-[#0A1128]/15 shadow-2xl shadow-[#0A1128]/25 flex flex-col animate-fade-in-up"
          data-testid="concierge-panel"
        >
          {/* HEADER */}
          <div className="bg-[#0A1128] text-[#F4F1EB] px-5 py-4 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/40 flex items-center justify-center shrink-0">
                <Sparkles size={16} strokeWidth={1.4} className="text-[#C5A059]" />
              </div>
              <div>
                <div className="font-serif-display text-lg font-light leading-tight">{t.header}</div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#C5A059] mt-0.5">
                  {t.subtitle}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[#F4F1EB]/70 hover:text-[#F4F1EB] p-1 -mr-1"
              aria-label="Close"
              data-testid="concierge-close"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
          </div>

          {/* MESSAGES */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
            data-testid="concierge-messages"
          >
            {messages.map((m, idx) => (
              <div
                key={`msg-${idx}-${m.role}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#0A1128] text-[#F4F1EB] rounded-2xl rounded-br-md"
                      : m.error
                        ? "bg-[#F4F1EB] text-[#8B4A4A] border border-[#8B4A4A]/30 rounded-2xl rounded-bl-md"
                        : "bg-[#F4F1EB] text-[#0A1128] rounded-2xl rounded-bl-md border border-[#0A1128]/10"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="concierge-thinking">
                <div className="bg-[#F4F1EB] text-[#3A435E] border border-[#0A1128]/10 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm">
                  <span className="inline-flex gap-1 items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] animate-bounce" style={{ animationDelay: "300ms" }} />
                    <span className="ml-2 text-xs text-[#3A435E]/70">{t.thinking}</span>
                  </span>
                </div>
              </div>
            )}
            {suggestMeeting && !sending && (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={gotoMeeting}
                  className="w-full bg-[#C5A059] text-[#0A1128] hover:bg-[#DCC287] transition-colors duration-300 px-4 py-3 text-xs tracking-[0.22em] uppercase"
                  data-testid="concierge-book-meeting"
                >
                  {t.book_meeting}
                </button>
              </div>
            )}
          </div>

          {/* INPUT */}
          <div className="border-t border-[#0A1128]/10 p-4 bg-[#FCFAF8]">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder={t.placeholder}
                rows={1}
                maxLength={1000}
                className="flex-1 resize-none bg-transparent border-b border-[#0A1128]/20 focus:border-[#C5A059] outline-none py-2 text-sm text-[#0A1128] placeholder:text-[#3A435E]/50"
                data-testid="concierge-input"
              />
              <button
                type="button"
                onClick={send}
                disabled={sending || !input.trim()}
                className="bg-[#0A1128] text-[#F4F1EB] hover:bg-[#162243] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300 p-2.5 shrink-0"
                data-testid="concierge-send"
                aria-label={t.send}
              >
                <Send size={16} strokeWidth={1.5} />
              </button>
            </div>
            <div className="text-[9px] tracking-[0.2em] uppercase text-[#3A435E]/60 mt-2">
              {t.powered}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConciergeChat;
